import { useCallback, useState } from 'react';
import type {
  FieldKeyedErrors,
  StructuredPartyActionError,
} from 'types/api-payloads';
import { actionErrorMessage, splitFieldErrors } from 'utils/api-errors';
import { ENTITY_FORM_ERROR_FIELDS } from './utils';

/**
 * A rejected EntityForm submit's errors: the ones the form can place on its
 * inputs go to `fieldErrors`, everything else to the `error` banner.
 * @return {object} The banner and field errors, and the handlers that set and clear them
 */
export const useEntityFormServerErrors = () => {
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldKeyedErrors>({});
  // Fires on every name keystroke; an unchanged state skips the re-render.
  const clearFieldErrors = useCallback(
    () =>
      setFieldErrors((current) =>
        Object.keys(current).length === 0 ? current : {}
      ),
    []
  );
  const clearErrors = useCallback(() => {
    setError(null);
    setFieldErrors({});
  }, []);
  const showServerError = useCallback(
    (err: StructuredPartyActionError | undefined, fallback: string) => {
      const { fieldErrors: inline, rest } = splitFieldErrors(
        err,
        ENTITY_FORM_ERROR_FIELDS
      );
      setFieldErrors(inline);
      const placedInline = Object.keys(inline).length > 0;
      if (rest || !placedInline) setError(actionErrorMessage(rest, fallback));
    },
    []
  );
  return {
    error,
    setError,
    fieldErrors,
    clearFieldErrors,
    clearErrors,
    showServerError,
  };
};
